import { Box, Grid } from '@mui/material'
import { Typography } from '@carto/meridian-ds/components'
import { useAppDispatch, useAppSelector } from 'app/state'
import { fetchColumnStatsRequest, setSettingsSnapshot } from 'features/analysis/state/analysisSlice'
import { AnalysisSettings, AnalysisType } from 'features/analysis/types'
import { getEnrichedDatasetById } from 'features/builder/state/keplerGlSlice'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import AnalysisBase from '../AnalysisBase'
import AnalysisHeader from '../AnalysisHeader'
import ColumnsDropdown from '../../Commons/Form/ColumnsDropdown'
import NumberFilterWrapper from './Form/NumberFilterWrapper'
import TextFilterWrapper from './Form/TextFilterWrapper'
import { generateFilterByColumnQuery } from './queries'
import { FilterByColumnSettings, FilterNumber, FilterText, FilterTypes } from './types'

export enum ColumnType {
  String = 'string',
  Text = 'text',
  Number = 'number',
  Float = 'float'
}

const supportedColumnsTypes = [ColumnType.String, ColumnType.Text, ColumnType.Number, ColumnType.Float]

const typeToFilter = {
  [ColumnType.String]: FilterTypes.Text,
  [ColumnType.Text]: FilterTypes.Text,
  [ColumnType.Number]: FilterTypes.Number,
  [ColumnType.Float]: FilterTypes.Number
}

function FilterByColumnForm({
  settings,
  onChange,
  onQueryChange
}: {
  settings: FilterByColumnSettings
  onChange: (analysis: AnalysisSettings) => void
  onQueryChange: (query: string | undefined) => void
}) {
  const dispatch = useAppDispatch()
  const sourceDatasetId = useAppSelector((state) => state.analysis.sourceDatasetId) || ''
  const sourceDataset = useAppSelector((state) => getEnrichedDatasetById(state, sourceDatasetId as string))
  const [sourceColumn, setSourceColumn] = useState<string>(settings?.sourceColumn || '')
  const [filterTextType, setFilterTextType] = useState<FilterText>(settings?.filterTextType || FilterText.Equal)
  const [filterNumberType, setFilterNumberType] = useState<FilterNumber>(
    settings?.filterNumberType || FilterNumber.Between
  )
  const [filterValue, setFilterValue] = useState(() => settings?.filterValue || [])
  const [filterNumberFirst, setFilterNumberFirst] = useState(settings?.filterNumberFirst)
  const [filterNumberSecond, setFilterNumberSecond] = useState(settings?.filterNumberSecond)
  const [filterType, setFilterType] = useState<FilterTypes>(() => settings?.filterType || FilterTypes.Number)

  useEffect(() => {
    if (filterType === FilterTypes.Text) {
      if (filterTextType && filterValue?.length) {
        onQueryChange(
          generateFilterByColumnQuery({
            sourceDataset: sourceDataset,
            sourceColumn,
            filterType,
            filterTextType,
            filterValue
          })
        )
      } else {
        onQueryChange(undefined)
      }
    } else {
      if (filterTextType && filterNumberFirst) {
        onQueryChange(
          generateFilterByColumnQuery({
            sourceDataset: sourceDataset,
            sourceColumn,
            filterType,
            filterNumberType,
            filterNumberFirst,
            filterNumberSecond
          })
        )
      } else {
        onQueryChange(undefined)
      }
    }
  }, [
    sourceDataset,
    filterNumberFirst,
    filterNumberSecond,
    filterNumberType,
    sourceColumn,
    filterType,
    filterTextType,
    filterValue,
    onQueryChange
  ])

  useEffect(() => {
    return () => {
      const filterSettings: FilterByColumnSettings = {
        mainSource: sourceDatasetId,
        sourceColumn: sourceColumn,
        filterType,
        filterTextType,
        filterValue,
        analysisType: AnalysisType.AddColumn
      }
      dispatch(setSettingsSnapshot(filterSettings))
      onChange(filterSettings)
    }
  }, [sourceDatasetId, sourceColumn, filterType, filterTextType, filterValue, onChange, dispatch])

  return (
    <>
      <AnalysisHeader analysisType={AnalysisType.ColumnValue} />
      <Box pt={2} pb={2}>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <AnalysisBase analysisType={AnalysisType.ColumnValue} />
            <Box mt={2} mb={1}>
              <ColumnsDropdown
                source={sourceDatasetId}
                supportedColumnsTypes={supportedColumnsTypes}
                labelId="analysis.columnValue.column"
                column={sourceColumn}
                onChange={(column, type) => {
                  setSourceColumn(column)
                  const columnType = type && typeToFilter[type]
                  type && setFilterType(columnType)
                  columnType === FilterTypes.Number &&
                    dispatch(
                      fetchColumnStatsRequest({
                        connection: sourceDataset.connectionName,
                        source: sourceDataset.source,
                        queryParameters: sourceDataset.queryParameters,
                        column,
                        type: sourceDataset.type
                      })
                    )
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box mb={1.5}>
              <Typography variant="subtitle2">
                <FormattedMessage id={`analysis.buffer.params`} />
              </Typography>
            </Box>
            {filterType === FilterTypes.Text ? (
              <TextFilterWrapper
                onFilterTextTypeChange={(value) => {
                  setFilterTextType(value)
                }}
                onFilterValueChange={(value) => {
                  setFilterValue(value)
                }}
              />
            ) : (
              <NumberFilterWrapper
                disabled={!sourceColumn}
                type={filterNumberType}
                onChange={(value) => {
                  setFilterNumberFirst(value?.filterNumberFirst)
                  setFilterNumberSecond(value?.filterNumberSecond)
                }}
                onNumberFilterTypeChange={(value) => {
                  setFilterNumberType(value)
                }}
              />
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export default FilterByColumnForm
